/**
 * Sent — the mail archive: every copy that left, newest first.
 *
 * This screen exists because the run record cannot answer the question that actually
 * gets asked. A run keeps the subject, the SES message ids and which grant went to
 * which recipient; it does not keep the message, and SES retains nothing once a send
 * is accepted. So the dispatcher archives both parts of every recipient's copy at send
 * time (CONTRACT-MAIL §1), and this is where an operator reads them back when a client
 * says "the figures in this look wrong" or "we never got it".
 *
 * Decisions worth keeping:
 *
 *  - **A bounce is encoded in form, not only colour.** Terminal statuses get a
 *    double-ruled badge, a danger left rule on the row, and a line of words under the
 *    subject saying it never arrived. One of those survives a greyscale screenshot
 *    pasted into a ticket; three of them survive anything.
 *  - **Date filters are a calendar, and only a calendar.** No "last 7 days" chips —
 *    that is a standing platform directive, and a relative preset in an archive is also
 *    a trap: the answer changes underneath a link someone pasted into a ticket.
 *  - **Long subjects clamp to two lines.** One 180-character subject must not set the
 *    height of every row in a 200-row list; the full text is on the title attribute.
 *  - **Filters and the open message both live in the query string**, so "this is the
 *    one that bounced" can be pasted to a colleague and land on the same message.
 *  - The list refreshes itself only while something is genuinely still moving: a
 *    message sits at `sent` until SES reports back, and that report arrives within
 *    minutes or not at all. Polling a week-old archive forever would be noise.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import Button from '@/components/Button';
import EmptyState from '@/components/EmptyState';
import { Checkbox, Input, Select } from '@/components/Field';
import { PageHeader } from '@/components/Layout';
import Badge from '@/components/Badge';
import MessageViewer, {
  MessageStatusBadge,
  RecipientKindBadge,
  isFailedStatus,
  messageStatusLabel,
} from '@/components/MessageViewer';
import Modal from '@/components/Modal';
import Pagination, { useCursorPagination } from '@/components/Pagination';
import Table, { type Column } from '@/components/Table';
import { api, errorMessage } from '@/lib/api';
import { formatCount, istDate, istDateTime, timeAgo, toDate, truncate } from '@/lib/format';
import { MESSAGE_STATUSES } from '@/lib/types';
import type { Client, Message, MessageStatus, Page } from '@/lib/types';

const POLL_MS = 15_000;

/**
 * How long a message is still expected to move. SES reports delivery or a bounce within
 * seconds to a few minutes; after that, a row that still says `sent` is telling us
 * something (no confirmation ever came), not waiting for a refresh.
 */
const STILL_MOVING_MS = 30 * 60_000;

function isMessageStatus(value: string): value is MessageStatus {
  return (MESSAGE_STATUSES as readonly string[]).includes(value);
}

export default function Sent() {
  const pager = useCursorPagination();
  const [params, setParams] = useSearchParams();

  const clientId = params.get('client_id') ?? '';
  const statusParam = params.get('status') ?? '';
  const status: MessageStatus | '' = isMessageStatus(statusParam) ? statusParam : '';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const openId = params.get('message') ?? '';
  // Test sends are excluded by the API, not by this screen (CONTRACT-RECIPIENTS §5); this
  // opts them back in. An operator checking what a client will get must not move that
  // client's delivery or open numbers, so exclusion is the default everywhere.
  const includeTest = params.get('include_test') === 'true';
  const filtered = Boolean(clientId || status || from || to || includeTest);

  const [clients, setClients] = useState<Client[]>([]);
  const [page, setPage] = useState<Page<Message>>({ items: [], cursor: null });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Which query the rows on screen belong to. A query never shown before gets the
  // skeleton; a repeat of the same one (a poll, a manual refresh) updates in place,
  // because a table that shimmers every fifteen seconds cannot be read.
  const shownQuery = useRef<string | null>(null);
  const queryKey = `${clientId}|${status}|${from}|${to}|${includeTest}|${pager.cursor ?? ''}|${pager.pageSize}`;

  // An inverted range returns nothing and looks like a broken screen, so it is named
  // rather than silently sent to the API.
  const rangeInverted = Boolean(from && to && from > to);

  useEffect(() => {
    if (rangeInverted) return;
    let cancelled = false;
    const isNewQuery = shownQuery.current !== queryKey;
    if (isNewQuery) setLoading(true);
    else setRefreshing(true);

    api.messages
      .list({
        cursor: pager.cursor,
        limit: pager.pageSize,
        client_id: clientId || undefined,
        status: status || undefined,
        from: from || undefined,
        to: to || undefined,
        include_test: includeTest || undefined,
      })
      .then((result) => {
        if (cancelled) return;
        setPage(result);
        setError(null);
        setRefreshedAt(new Date());
        shownQuery.current = queryKey;
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(errorMessage(err));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        setRefreshing(false);
      });

    return () => {
      cancelled = true;
    };
    // queryKey encodes every filter and the cursor; reloadKey forces a re-fetch.
  }, [
    queryKey,
    reloadKey,
    rangeInverted,
    clientId,
    status,
    from,
    to,
    includeTest,
    pager.cursor,
    pager.pageSize,
  ]);

  useEffect(() => {
    let cancelled = false;
    api.clients
      .listAll()
      .then((all) => {
        if (!cancelled) setClients(all);
      })
      .catch(() => {
        // A failed client list costs the filter its labels, not the screen its rows.
        if (!cancelled) setClients([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const clientName = useCallback(
    (message: Message) =>
      message.client_name ??
      clients.find((entry) => entry.client_id === message.client_id)?.name ??
      message.client_id,
    [clients],
  );

  const stillMoving = useMemo(() => {
    const now = Date.now();
    return page.items.some((message) => {
      if (message.status !== 'sent') return false;
      const sent = toDate(message.sent_at)?.getTime();
      return sent !== undefined && now - sent < STILL_MOVING_MS;
    });
  }, [page.items]);

  useEffect(() => {
    if (!stillMoving) return;
    const timer = window.setInterval(() => setReloadKey((key) => key + 1), POLL_MS);
    return () => window.clearInterval(timer);
  }, [stillMoving]);

  const setFilter = (
    key: 'client_id' | 'status' | 'from' | 'to' | 'include_test',
    value: string,
  ): void => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete('message');
    setParams(next, { replace: true });
    pager.reset();
  };

  const clearFilters = (): void => {
    setParams(new URLSearchParams(), { replace: true });
    pager.reset();
  };

  const openMessage = (message: Message): void => {
    const next = new URLSearchParams(params);
    next.set('message', message.message_id);
    setParams(next);
  };

  const closeMessage = (): void => {
    const next = new URLSearchParams(params);
    next.delete('message');
    setParams(next, { replace: true });
  };

  const openSummary = useMemo(
    () => page.items.find((message) => message.message_id === openId) ?? null,
    [openId, page.items],
  );

  const columns: Column<Message>[] = [
    {
      // Rank 1. State goes first on every table in this console, so it survives any
      // overflow that gets through.
      key: 'status',
      header: 'Status',
      width: '132px',
      nowrap: true,
      render: (message) => (
        <div className="stack-tight" style={{ alignItems: 'flex-start' }}>
          <span className="cluster-tight">
            <MessageStatusBadge status={message.status} />
            {/* A test copy is marked in the list as well as in the filter: a row that is
                in the table only because someone ticked a box must say so on its own.
                It is a classification, not a state, so it is a mark and not a pill. */}
            {message.is_test ? (
              <Badge
                variant="plain"
                glyph="⚗"
                title="A test send to an internal address — left out of this client's delivery and open figures"
              >
                Test
              </Badge>
            ) : null}
          </span>
          {/* The words belong in the same cell as the badge they restate. They used to sit
              in the Subject cell, ~500px away from the status that says Bounced. */}
          {isFailedStatus(message.status) ? (
            <span className="xs strong" style={{ color: 'var(--danger)' }}>
              ⊘ Never delivered
            </span>
          ) : message.open_count > 0 ? (
            <span
              className="xs dim"
              title="Open tracking is a pixel fetch — a mail app that blocks images reads the message without reporting one"
            >
              opened {formatCount(message.open_count)}×
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'sent',
      header: 'Sent (IST)',
      nowrap: true,
      width: '158px',
      // One line. The relative time is a supplement (CONTRACT §8.7), so it moves to the
      // title and the absolute IST value stays on screen.
      render: (message) => (
        <span title={`${istDateTime(message.sent_at)} · ${timeAgo(message.sent_at)}`}>
          {istDateTime(message.sent_at)}
        </span>
      ),
    },
    {
      key: 'client',
      header: 'Client',
      width: '150px',
      render: (message) => (
        <div
          className="truncate"
          title={`${clientName(message)} · ${message.client_id}`}
        >
          {clientName(message)}
        </div>
      ),
    },
    {
      key: 'subject',
      header: 'Subject',
      width: '240px',
      render: (message) => (
        <div className="truncate" title={message.subject}>
          {message.subject}
        </div>
      ),
    },
    {
      // The only width-less column, so it absorbs every remaining pixel. A recipient
      // address is never truncated, masked or clipped — two recipients at one long
      // domain used to render as identical strings on the screen whose job is
      // "we never got it".
      key: 'recipient',
      header: 'Recipient',
      nowrap: true,
      render: (message) => (
        <span className="cluster-tight" style={{ minWidth: 0 }}>
          <span className="mono small">{message.recipient_email}</span>
          {/* to is the answer on nearly every row; the badge exists to explain a cc or a
              bcc, so it renders only when there is something to explain. */}
          {message.recipient_kind !== 'to' ? (
            <RecipientKindBadge kind={message.recipient_kind} />
          ) : null}
        </span>
      ),
    },
    {
      key: 'links',
      header: 'Link window (IST)',
      width: '176px',
      nowrap: true,
      optional: true,
      headerTitle:
        'Whether the links inside this copy still work — our 7-day window, never an S3 signature. An upper bound: a client with a shorter TTL expires sooner, and a revoked link shows on the run.',
      render: (message) => {
        if (typeof message.links_expire_at !== 'number') {
          return (
            <span
              className="dim"
              title="No expiry recorded for this copy — open the run to see the grants it issued"
            >
              —
            </span>
          );
        }
        // NOT LinkBadge. This row carries an expiry, not a grant state: revocation is per
        // link and lives on the run's grant table. Reusing the badge with revoked_at
        // hardcoded to null printed a green "Live" on a dispatch whose grants had all
        // been revoked, and a wrong state is worse than a missing one.
        const closed = message.links_expire_at * 1000 <= Date.now();
        return (
          <Badge
            tone={closed ? 'muted' : 'neutral'}
            glyph={closed ? '⊘' : '◷'}
            title={
              closed
                ? 'The 7-day window on this copy has closed. Individual links may have been revoked earlier — the run\'s grant table is the authority.'
                : "The window on this copy is still open. Individual links may have been revoked — the run's grant table is the authority."
            }
          >
            {closed ? 'Closed ' : 'Open until '}
            {istDate(message.links_expire_at)}
          </Badge>
        );
      },
    },
  ];

  return (
    <div className="stack">
      <PageHeader
        title="Sent"
        subtitle={
          <>
            Every copy we handed to SES, exactly as it was sent.{' '}
            {refreshedAt ? (
              <span className="dim">
                Updated {istDateTime(refreshedAt)}
                {stillMoving ? ' · auto-refreshing while delivery is unconfirmed' : ''}
              </span>
            ) : null}
          </>
        }
        actions={
          <Button
            onClick={() => setReloadKey((key) => key + 1)}
            loading={refreshing}
            icon={<span aria-hidden="true">↻</span>}
          >
            Refresh
          </Button>
        }
      />

      <div className="toolbar">
        <label className="cluster-tight">
          <span className="small dim">Client</span>
          <Select
            size="sm"
            value={clientId}
            aria-label="Filter by client"
            onChange={(event) => setFilter('client_id', event.target.value)}
          >
            <option value="">All clients</option>
            {clients.map((client) => (
              <option key={client.client_id} value={client.client_id}>
                {client.name}
              </option>
            ))}
          </Select>
        </label>

        <label className="cluster-tight">
          <span className="small dim">Status</span>
          <Select
            size="sm"
            value={status}
            aria-label="Filter by status"
            onChange={(event) => setFilter('status', event.target.value)}
          >
            <option value="">Any status</option>
            {MESSAGE_STATUSES.map((value) => (
              <option key={value} value={value}>
                {messageStatusLabel(value)}
              </option>
            ))}
          </Select>
        </label>

        <label className="cluster-tight small">
          <span className="dim">Sent from (IST)</span>
          <Input
            size="sm"
            type="date"
            value={from}
            aria-label="Sent on or after this IST date"
            style={{ maxWidth: '150px' }}
            onChange={(event) => setFilter('from', event.target.value)}
          />
        </label>

        <label className="cluster-tight small">
          <span className="dim">Sent to (IST)</span>
          <Input
            size="sm"
            type="date"
            value={to}
            aria-label="Sent on or before this IST date"
            style={{ maxWidth: '150px' }}
            onChange={(event) => setFilter('to', event.target.value)}
          />
        </label>

        <Checkbox
          checked={includeTest}
          onChange={(event) => setFilter('include_test', event.target.checked ? 'true' : '')}
          label={<span className="small dim">Include test sends</span>}
        />

        {filtered ? (
          <Button size="sm" variant="ghost" onClick={clearFilters}>
            Clear filters
          </Button>
        ) : null}

        <span className="grow" />

        <span className="xs dim">Dates are IST calendar days.</span>
      </div>

      {rangeInverted ? (
        <div className="callout tone-warn" role="alert">
          <span className="callout-glyph" aria-hidden="true">
            ▲
          </span>
          <span>
            The start date is after the end date, so nothing can match. Swap them, or clear one.
          </span>
        </div>
      ) : null}

      <Table
        columns={columns}
        rows={rangeInverted ? [] : page.items}
        rowKey={(message) => message.message_id}
        loading={loading && !rangeInverted}
        dense
        stickyHeader
        onRowClick={openMessage}
        isRowActive={(message) => message.message_id === openId}
        rowTone={(message) => (isFailedStatus(message.status) ? 'danger' : 'default')}
        empty={
          error ? (
            <EmptyState
              tone="danger"
              title="Could not load sent mail"
              description={error}
              action={
                <Button variant="primary" onClick={() => setReloadKey((key) => key + 1)}>
                  Retry
                </Button>
              }
            />
          ) : filtered ? (
            <EmptyState
              tone="filtered"
              title="No messages match those filters"
              description="Nothing was sent for that client, status and date range."
              action={
                <Button variant="primary" onClick={clearFilters}>
                  Clear filters
                </Button>
              }
            />
          ) : (
            <EmptyState
              title="Nothing sent yet"
              description="Every recipient's copy is archived the moment it is handed to SES. The first dispatch will appear here."
            />
          )
        }
        footer={
          <Pagination
            page={pager.page}
            pageSize={pager.pageSize}
            count={rangeInverted ? 0 : page.items.length}
            hasNext={Boolean(page.cursor) && !rangeInverted}
            hasPrev={pager.hasPrev}
            onNext={() => pager.next(page.cursor)}
            onPrev={pager.prev}
            onPageSize={pager.setPageSize}
            loading={loading || refreshing}
            label="messages"
          />
        }
      />

      {error && page.items.length > 0 ? (
        <div className="callout tone-danger" role="alert">
          <span className="callout-glyph" aria-hidden="true">
            ✕
          </span>
          <span>{error} Showing the last rows that loaded — they may be out of date.</span>
        </div>
      ) : null}

      <Modal
        open={Boolean(openId)}
        onClose={closeMessage}
        size="xl"
        title={openSummary ? truncate(openSummary.subject, 90) : 'Sent message'}
        description={
          openSummary ? (
            <>
              To <span className="mono">{openSummary.recipient_email}</span> ·{' '}
              {istDateTime(openSummary.sent_at)}
            </>
          ) : (
            'The copy as it was archived at send time.'
          )
        }
        footer={
          <Button variant="ghost" onClick={closeMessage}>
            Close
          </Button>
        }
      >
        {openId ? <MessageViewer messageId={openId} summary={openSummary} /> : null}
      </Modal>
    </div>
  );
}
